import axios from 'axios';
import {defineStore} from 'pinia';
import {getLocalStorage, setLocalStorage, checkDocument, scrollTop} from '@js/helpers.ts';
import {useThemeStore} from "@js/store/ThemeStore.js";
import {ecomAdd, ecomRemove, ecomPurchase, mapProduct} from '@js/ecommerce.js';

const COOKIE_CART = 'cart_metal_v2';
const COOKIE_UI = 'cart_ui_metal_v2';
const COOKIE_OPTS = 'cart_opts_metal_v2';
const YEAR_IN_MINUTES = 525600;

function roundMoney(v) {
    return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
}

function roundPieces(v) {
    return Math.max(1, Math.round(Number(v)));
}

function roundArea(v) {
    return Number(Number(v || 0).toFixed(2));
}

export const useCartStore = defineStore('cartStore', {
    state: () => ({
        catalog: {},

        ui: {},

        // opts[id] = {
        //   price_id: number|null,
        //   specification_value_ids: { [specification_type_id]: specification_value_id }
        // }
        opts: {},

        items: [],

        loadings: {
            cart_loading: true,
            sending: false,
        },

        isCartClearModalVisible: false,
        cart_products: [],

        form: {
            full_name: '',
            phone: '',
            email: '',
            inn: '',
            comment: '',
            captcha_token: '',

            delivery_type: 'pickup',
            delivery_address: '',

            vehicle_type: null,
            vehicle: null,
            payment_type: 'cash',
        },

        cart_form_successfully_sent: false,
        form_errors: [],
    }),

    getters: {
        productOf: (s) => (id) => s.catalog[Number(id)] ?? null,

        uiOf: (s) => (id) => s.ui[Number(id)] ?? {qty: 1},

        step() {
            return () => 1;
        },

        minQty() {
            return () => 1;
        },

        getSelection() {
            return (id) => this.opts[Number(id)] ?? {
                price_id: null,
                specification_value_ids: {},
            };
        },

        currentPriceEntry() {
            return (id) => {
                const p = this.productOf(id);
                if (!p) return null;

                const prices = Array.isArray(p.prices) ? p.prices : [];
                if (!prices.length) return null;

                const sel = this.getSelection(id);
                const selected = prices.find(price => Number(price.id) === Number(sel.price_id));

                return selected || prices[0] || null;
            };
        },

        specificationGroups() {
            return (id) => {
                const price = this.currentPriceEntry(id);
                if (!price) return [];

                const grouped = {};

                for (const row of (price.specification_values || [])) {
                    const type = row?.specification_type || {};
                    const value = row?.specification_value || {};

                    const typeId = Number(type.id || 0);
                    const typeTitle = String(type.title || '').trim();
                    const valueId = Number(value.id || 0);
                    const valueTitle = String(value.value || '').trim();
                    const delta = Number(row?.price_delta || 0);

                    if (!typeId || !valueId || !typeTitle || !valueTitle) continue;

                    if (!grouped[typeId]) {
                        grouped[typeId] = {
                            type_id: typeId,
                            type_name: typeTitle,
                            values: [],
                        };
                    }

                    grouped[typeId].values.push({
                        id: valueId,
                        value: valueTitle,
                        price_delta: delta,
                        image_url: row.specification_value.image_url ?? null,
                    });
                }

                return Object.values(grouped);
            };
        },

        selectedSpecValueId() {
            return (id, typeId) => {
                const sel = this.getSelection(id);
                const value = sel?.specification_value_ids?.[typeId];
                return value == null ? null : Number(value);
            };
        },

        priceActive() {
            return (id) => {
                const price = this.currentPriceEntry(id);
                if (!price) return 0;

                let total = Number(price.base_price || 0);

                const groups = this.specificationGroups(id);
                const selectedMap = this.getSelection(id)?.specification_value_ids || {};

                for (const group of groups) {
                    const selectedId = selectedMap[group.type_id];
                    if (selectedId == null) continue;

                    const found = (group.values || []).find(v => Number(v.id) === Number(selectedId));
                    if (found) {
                        total += Number(found.price_delta || 0);
                    }
                }

                return roundMoney(total);
            };
        },

        unitLabel() {
            return (id) => {
                const price = this.currentPriceEntry(id);
                return price?.price_type?.short_title || 'шт';
            };
        },

        isAvailable() {
            return (id) => !!this.productOf(id)?.available;
        },

        findItem: (s) => (id) => s.items.find(i => Number(i.id) === Number(id)),

        isInCartExact() {
            return (id) => !!this.findItem(id);
        },

        isInCartAny() {
            return (id) => !!this.findItem(id);
        },

        totalPrice() {
            return (id) => roundMoney(this.priceActive(id) * Number(this.uiOf(id).qty || 0));
        },

        subTotal(state) {
            return roundMoney(state.items.reduce((sum, it) => {
                const price = Number(this.priceActive(it.id) || 0);
                return sum + price * Number(it.qty);
            }, 0));
        },

        weightKg() {
            return (id) => {
                const qty = roundPieces(this.uiOf(id).qty);
                const p = this.productOf(id);
                const perPiece = Number(p?.weight ?? p?.tech?.weight ?? 0);
                if (!perPiece) return null;
                return Number((qty * perPiece).toFixed(3));
            };
        },

        weightTotal(state) {
            const sum = state.items.reduce((acc, it) => {
                const p = this.productOf(it.id);
                const perPiece = Number(p?.weight ?? p?.tech?.weight ?? 0);
                if (!perPiece) return acc;
                return acc + roundPieces(it.qty) * perPiece;
            }, 0);

            return Number(sum.toFixed(3));
        },

        areaPerPiece() {
            return (id) => {
                const p = this.productOf(id);

                const L = Number(p?.length ?? p?.tech?.length ?? 0);
                const W = Number(p?.width ?? p?.tech?.width ?? 0);

                if (!L || !W) return null;

                return roundArea(L * W);
            };
        },

        areaSqM() {
            return (id) => {
                const perPiece = this.areaPerPiece(id);
                if (perPiece == null) return null;

                const qty = roundPieces(this.uiOf(id).qty);
                return roundArea(perPiece * qty);
            };
        },

        totalArea(state) {
            const sum = state.items.reduce((acc, it) => {
                const p = this.productOf(it.id);

                const L = Number(p?.length ?? p?.tech?.length ?? 0);
                const W = Number(p?.width ?? p?.tech?.width ?? 0);

                if (!L || !W) return acc;

                return acc + (L * W * roundPieces(it.qty));
            }, 0);

            return roundArea(sum);
        },
    },

    actions: {
        _mapForEcom(id, qty, list = 'Cart') {
            const p = this.productOf(id);
            if (!p) return null;

            const mp = mapProduct(p, {list, position: 1, qty: Number(qty || 1)});
            mp.variant = this.unitLabel(id);

            return mp;
        },

        _trackCartDelta(id, prevQty, nextQty) {
            const p = this.productOf(id);
            if (!p || prevQty == null || nextQty == null) return;

            const diff = Number(nextQty) - Number(prevQty);
            if (diff === 0) return;

            const qtyAbs = Math.abs(diff);
            const mp = this._mapForEcom(id, qtyAbs, 'Cart');
            if (!mp) return;

            try {
                if (diff > 0) ecomAdd(mp);
                else ecomRemove(mp);
            } catch {}
        },

        hydrate() {
            this.loadCart();
            this.loadUI();
            this.loadOpts();
            this._persistUIFiltered();
        },

        registerCatalogProduct(product) {
            const id = Number(product.id);
            const prices = Array.isArray(product.prices) ? product.prices : [];

            this.catalog[id] = {
                id: product.id,
                title: product.title,
                slug: product.slug,
                category_slug: product?.category?.slug ?? product?.category_slug ?? null,

                available: !!product.available,
                images: product.images ?? product?.media?.images ?? [],
                weight: product?.tech?.weight ?? product?.weight ?? null,
                length: product?.tech?.length ?? product?.length ?? null,
                width: product?.tech?.width ?? product?.width ?? null,
                tech: product?.tech ?? {},

                prices,
            };

            if (!this.ui[id]) {
                this.ui[id] = {qty: 1};
            }

            this.ensureSelectionInitialized(id);
        },

        ensureSelectionInitialized(id) {
            id = Number(id);
            const product = this.productOf(id);
            if (!product) return;

            const prices = Array.isArray(product.prices) ? product.prices : [];
            if (!prices.length) return;

            const existing = this.opts[id] ?? {
                price_id: null,
                specification_value_ids: {},
            };

            let price = prices.find(p => Number(p.id) === Number(existing.price_id));
            if (!price) {
                price = prices[0];
            }

            const grouped = {};

            for (const row of (price.specification_values || [])) {
                const type = row?.specification_type || {};
                const value = row?.specification_value || {};

                const typeId = Number(type.id || 0);
                const valueId = Number(value.id || 0);

                if (!typeId || !valueId) continue;

                if (!grouped[typeId]) grouped[typeId] = [];
                grouped[typeId].push(valueId);
            }

            const specification_value_ids = {...(existing.specification_value_ids || {})};

            for (const typeId of Object.keys(grouped)) {
                const numericTypeId = Number(typeId);

                // если ранее выбора не было — оставляем null
                if (!(numericTypeId in specification_value_ids)) {
                    specification_value_ids[numericTypeId] = null;
                    continue;
                }

                // если выбранное значение больше не существует — сбрасываем в null
                const selectedId = specification_value_ids[numericTypeId];
                if (selectedId != null && !grouped[numericTypeId].includes(Number(selectedId))) {
                    specification_value_ids[numericTypeId] = null;
                }
            }

            // убираем значения групп, которых больше нет у текущей цены
            for (const existingTypeId of Object.keys(specification_value_ids)) {
                const numericTypeId = Number(existingTypeId);
                if (!grouped[numericTypeId]) {
                    delete specification_value_ids[numericTypeId];
                }
            }

            this.opts[id] = {
                price_id: Number(price.id),
                specification_value_ids,
            };

            this.persistOpts();
        },

        setPrice(id, priceId) {
            id = Number(id);
            priceId = Number(priceId);

            const product = this.productOf(id);
            if (!product) return;

            const price = (product.prices || []).find(p => Number(p.id) === priceId);
            if (!price) return;

            const current = this.getSelection(id);
            const currentSpecValues = {...(current.specification_value_ids || {})};

            const grouped = {};

            for (const row of (price.specification_values || [])) {
                const typeId = Number(row?.specification_type?.id || 0);
                const valueId = Number(row?.specification_value?.id || 0);

                if (!typeId || !valueId) continue;

                if (!grouped[typeId]) grouped[typeId] = [];
                grouped[typeId].push(valueId);
            }

            const nextSpecValues = {};

            for (const typeId of Object.keys(grouped)) {
                const numericTypeId = Number(typeId);
                const prevSelected = currentSpecValues[numericTypeId];

                if (prevSelected != null && grouped[numericTypeId].includes(Number(prevSelected))) {
                    nextSpecValues[numericTypeId] = Number(prevSelected);
                } else {
                    nextSpecValues[numericTypeId] = null;
                }
            }

            this.opts[id] = {
                price_id: priceId,
                specification_value_ids: nextSpecValues,
            };

            this.persistOpts();
        },

        setSpecificationValue(id, specificationTypeId, specificationValueId) {
            id = Number(id);
            specificationTypeId = Number(specificationTypeId);

            const normalizedValueId =
                specificationValueId == null || specificationValueId === ''
                    ? null
                    : Number(specificationValueId);

            const current = this.getSelection(id);

            this.opts[id] = {
                price_id: current.price_id != null ? Number(current.price_id) : null,
                specification_value_ids: {
                    ...(current.specification_value_ids || {}),
                    [specificationTypeId]: normalizedValueId,
                },
            };

            this.persistOpts();
        },

        loadUI() {
            const raw = getLocalStorage(COOKIE_UI) || '{}';
            try {
                const obj = JSON.parse(raw);
                this.ui = (obj && typeof obj === 'object') ? obj : {};
            } catch {
                this.ui = {};
            }
        },

        _persistUIFiltered() {
            const tracked = new Set(this.items.map(i => Number(i.id)));
            const toSave = {};

            for (const [k, v] of Object.entries(this.ui)) {
                const id = Number(k);
                if (tracked.has(id)) toSave[k] = v;
            }

            setLocalStorage(COOKIE_UI, JSON.stringify(toSave), YEAR_IN_MINUTES);
        },

        _syncCartLineIfExists(id) {
            const ui = this.uiOf(id);
            const line = this.findItem(id);
            if (!line) return;

            const prev = line.qty;
            line.qty = roundPieces(ui.qty);

            this._trackCartDelta(id, prev, line.qty);

            if (line.qty <= 0) this.remove(id);

            this.persistCart();
            this._persistUIFiltered();
        },

        incQty(id) {
            const ui = this.ui[id];
            if (!ui) return;

            const line = this.findItem(id);
            const prev = line ? line.qty : null;

            ui.qty = roundPieces(ui.qty + 1);
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id)?.qty ?? null;
                this._trackCartDelta(id, prev, next);
            }
        },

        decQty(id) {
            const ui = this.ui[id];
            if (!ui) return;

            const line = this.findItem(id);
            const prev = line ? line.qty : null;

            ui.qty = roundPieces(Math.max(1, ui.qty - 1));
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id)?.qty ?? null;
                this._trackCartDelta(id, prev, next);
            }
        },

        setQty(id, v) {
            const ui = this.ui[id];
            if (!ui) return;

            const num = Number(v);
            if (Number.isNaN(num)) return;

            const line = this.findItem(id);
            const prev = line ? line.qty : null;

            ui.qty = roundPieces(Math.max(1, num));
            this._syncCartLineIfExists(id);

            if (line) {
                const next = this.findItem(id)?.qty ?? null;
                this._trackCartDelta(id, prev, next);
            }
        },

        setQtyFromInput(id, raw) {
            this.setQty(id, String(raw).replace(',', '.'));
        },

        loadCart() {
            const raw = getLocalStorage(COOKIE_CART) || '[]';

            try {
                const arr = JSON.parse(raw);
                this.items = Array.isArray(arr)
                    ? arr
                        .map(r => {
                            const id = Number(r.id);
                            const qty = roundPieces(r.qty);
                            if (!id || !qty) return null;
                            return {id, qty};
                        })
                        .filter(Boolean)
                    : [];
            } catch {
                this.items = [];
            }
        },

        loadOpts() {
            const raw = getLocalStorage(COOKIE_OPTS) || '{}';
            try {
                this.opts = JSON.parse(raw) || {};
            } catch {
                this.opts = {};
            }
        },

        persistOpts() {
            setLocalStorage(COOKIE_OPTS, JSON.stringify(this.opts), YEAR_IN_MINUTES);
        },

        getCartData() {
            this.loadings.cart_loading = true;

            axios.post('/axios/products/get-by-ids', {
                ids: this.items.map(i => i.id) ?? [],
            })
                .then(response => {
                    const arr = response?.data?.products ?? [];

                    for (const p of arr) {
                        this.registerCatalogProduct(p);
                    }

                    this.cart_products = arr;
                    this.loadings.cart_loading = false;
                })
                .catch(() => {
                    this.loadings.cart_loading = false;
                });
        },

        persistCart() {
            setLocalStorage(COOKIE_CART, JSON.stringify(this.items), YEAR_IN_MINUTES);
        },

        canAddToCart(id) {
            return this.isAvailable(id) && this.priceActive(id) > 0;
        },

        addToCartFromCard(id) {
            id = Number(id);
            if (!this.canAddToCart(id)) return;

            const normalizedQty = roundPieces(this.uiOf(id).qty);
            const existing = this.findItem(id);

            if (existing) {
                const prev = existing.qty;
                existing.qty = roundPieces(existing.qty + normalizedQty);

                const mp = this._mapForEcom(id, (existing.qty - prev), 'Cart');
                if (mp) {
                    try {
                        ecomAdd(mp);
                    } catch {}
                }
            } else {
                this.items.push({id, qty: normalizedQty});

                const mp = this._mapForEcom(id, normalizedQty, 'Cart');
                if (mp) {
                    try {
                        ecomAdd(mp);
                    } catch {}
                }
            }

            this.persistCart();
            this._persistUIFiltered();

            const themeStore = useThemeStore();
            themeStore.addToast("Добавлено в смету", "success", {route_name: 'cart'}, 5000);
        },

        updateItemQty(id, _mode, qty) {
            const it = this.findItem(Number(id));
            if (!it) return;

            const prev = it.qty;
            it.qty = roundPieces(qty);

            if (it.qty <= 0) {
                const mp = this._mapForEcom(Number(id), prev, 'Cart');
                if (mp) {
                    try {
                        ecomRemove(mp);
                    } catch {}
                }
                this.remove(id);
                return;
            }

            this._trackCartDelta(Number(id), prev, it.qty);
            this.persistCart();
            this._persistUIFiltered();
        },

        remove(id, _mode) {
            const it = this.findItem(Number(id));
            if (it) {
                const mp = this._mapForEcom(Number(id), it.qty, 'Cart');
                if (mp) {
                    try {
                        ecomRemove(mp);
                    } catch {}
                }
            }

            this.items = this.items.filter(i => Number(i.id) !== Number(id));
            this.persistCart();

            const stillHas = this.items.some(i => Number(i.id) === Number(id));
            if (!stillHas) {
                delete this.ui[id];
                delete this.opts[id];
                this.persistOpts();
            }

            this._persistUIFiltered();
            this.getCartData();
        },

        clear() {
            try {
                for (const it of this.items) {
                    const mp = this._mapForEcom(it.id, it.qty, 'Cart');
                    if (mp) ecomRemove(mp);
                }
            } catch {}

            this.items = [];
            this.persistCart();

            this.ui = {};
            this._persistUIFiltered();

            this.opts = {};
            this.persistOpts();

            this.getCartData();
        },

        sendForm() {
            if (!checkDocument()) return;

            if (this.loadings.sending) return;

            this.form_errors = [];
            this.loadings.sending = true;
            this.cart_form_successfully_sent = false;
            this.form.captcha_token = '';

            const captchaId = 'smart-captcha-container-cart-form';

            const existing = document.getElementById(captchaId);
            if (existing) {
                try {
                    window.smartCaptcha.destroy(existing.dataset.widgetId);
                } catch (e) {}
                existing.remove();
            }

            const container = document.createElement('div');
            container.id = captchaId;
            container.style.position = 'absolute';
            container.style.zIndex = '-1';
            container.style.left = '-9999px';
            container.style.top = '-9999px';
            document.body.appendChild(container);

            let tokenReceived = false;

            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true;
                    this.form.captcha_token = token;

                    const payloadItems = this.items.map(it => ({
                        id: it.id,
                        qty: it.qty,
                        opts: this.getSelection(it.id),
                    }));

                    axios.post('/axios/form/send-lead', {
                        bitrix_title: 'Оформление заказа на сайте ФУЛЛ СТРОЙ',
                        ...this.form,
                        cart_products: payloadItems,
                    }).then(response => {
                        scrollTop();
                        this.loadings.sending = false;
                        this.cart_form_successfully_sent = true;

                        try {
                            const orderId = response?.data?.order_id ?? null;
                            const revenue = typeof response?.data?.revenue === 'number'
                                ? response.data.revenue
                                : undefined;

                            if (orderId) {
                                const products = this.items.map((it, idx) => {
                                    const p = this.productOf(it.id);
                                    const mp = mapProduct(p, {
                                        list: 'Purchase',
                                        position: idx + 1,
                                        qty: Number(it.qty || 1)
                                    });
                                    mp.variant = this.unitLabel(it.id);
                                    return mp;
                                });

                                ecomPurchase({orderId, revenue, products});
                            }
                        } catch {}

                        this.clear();

                        try {
                            window.smartCaptcha.destroy(widgetId);
                        } catch (e) {}
                    }).catch(error => {
                        this.loadings.sending = false;

                        if (error.response?.status === 422) {
                            this.form_errors = error.response.data.errors;
                        }
                    });
                }
            });

            document.getElementById(captchaId).dataset.widgetId = widgetId;

            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.loadings.sending = false;
                    this.form_errors = {captcha_token: ['Вы закрыли капчу. Повторите попытку.']};

                    try {
                        window.smartCaptcha.reset(widgetId);
                    } catch (e) {}
                }
            });

            setTimeout(() => {
                try {
                    window.smartCaptcha.execute(widgetId);
                } catch (e) {
                    this.loadings.sending = false;
                    this.form_errors = {captcha_token: ['Не удалось запустить капчу. Попробуйте снова.']};
                }
            }, 100);
        },

        setVehicle(id) {
            this.form.vehicle = id;
        },
    }
});
